import type {
  AppointmentListItem,
  AppointmentPastWeekResponse,
} from "../../../shared/api-types.js";

/**
 * The Past view (NO-767): one device-local Monday-to-Sunday week of ended
 * Appointments. Everything here is pure so the copy, the week math, and the
 * cache decisions are tested without a browser. A week is named by its ISO
 * Monday, `YYYY-MM-DD`, and all week math is calendar math, so it is DST-safe.
 */
export interface PastWeekClock {
  now: string;
  timezone: string;
}

const ISO_DATE_RE = /^(\d{4})-(\d{2})-(\d{2})$/;
const MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];
const DASH = " – ";
const APOSTROPHE = "’";

interface CalendarDate {
  year: number;
  month: number;
  day: number;
}

function parseCalendarDate(value: string): CalendarDate | null {
  const match = ISO_DATE_RE.exec(value);
  if (!match) return null;

  const [year, month, day] = match.slice(1).map(Number) as [number, number, number];
  const date = new Date(Date.UTC(year, month - 1, day));
  const isRealDate =
    date.getUTCFullYear() === year &&
    date.getUTCMonth() === month - 1 &&
    date.getUTCDate() === day;

  return isRealDate ? { year, month, day } : null;
}

function calendarDateOf(value: string): CalendarDate {
  const parsed = parseCalendarDate(value);
  if (!parsed) throw new RangeError("Not a calendar date");

  return parsed;
}

function formatCalendarDate(date: Date): string {
  return date.toISOString().slice(0, 10);
}

/** A real calendar date written YYYY-MM-DD that falls on a Monday. */
export function isIsoMondayDate(value: unknown): value is string {
  if (typeof value !== "string") return false;

  const parsed = parseCalendarDate(value);
  if (!parsed) return false;

  return (
    new Date(Date.UTC(parsed.year, parsed.month - 1, parsed.day)).getUTCDay() === 1
  );
}

/** The calendar date (YYYY-MM-DD) of an instant in `timezone`. */
export function calendarDateInTimezone(instant: string, timezone: string): string {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: timezone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(new Date(instant));
  const read = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find((part) => part.type === type)?.value;

  return `${read("year")}-${read("month")}-${read("day")}`;
}

function addDays(value: string, days: number): string {
  const { year, month, day } = calendarDateOf(value);

  return formatCalendarDate(new Date(Date.UTC(year, month - 1, day + days)));
}

/** The ISO Monday of `now` in `timezone`: the current week. */
export function currentWeekStart(now: string, timezone: string): string {
  const today = calendarDateInTimezone(now, timezone);
  const { year, month, day } = calendarDateOf(today);
  const weekday = new Date(Date.UTC(year, month - 1, day)).getUTCDay();

  return addDays(today, -((weekday + 6) % 7));
}

/**
 * NO-907: the week the Week view opens on, and what Today returns to. The
 * current week on weekdays; on Saturday and Sunday the coming one, because the
 * week is over and an RD's newly saved hours apply from it.
 */
export function defaultWeekStart(now: string, timezone: string): string {
  const current = currentWeekStart(now, timezone);
  const today = calendarDateInTimezone(now, timezone);

  return today > addDays(current, 4) ? addDays(current, 7) : current;
}

export function earlierWeek(week: string): string {
  return addDays(week, -7);
}

export function laterWeek(week: string): string {
  return addDays(week, 7);
}

/** A week after the current one has nothing ended, so it is never requested. */
export function isFutureWeek(week: string, clock: PastWeekClock): boolean {
  return week > currentWeekStart(clock.now, clock.timezone);
}

/**
 * "Oct 5 – 11", "Sep 28 – Oct 4", "Nov 17 – 23, 2025", or
 * "Dec 29, 2025 – Jan 4, 2026". The year shows only outside the current year,
 * which is the year of `now` in the timezone.
 */
export function weekRangeLabel(week: string, clock: PastWeekClock): string {
  const monday = calendarDateOf(week);
  const sunday = calendarDateOf(addDays(week, 6));
  const mondayMonth = MONTHS[monday.month - 1];
  const sundayMonth = MONTHS[sunday.month - 1];

  if (monday.year !== sunday.year) {
    return `${mondayMonth} ${monday.day}, ${monday.year}${DASH}${sundayMonth} ${sunday.day}, ${sunday.year}`;
  }

  const currentYear = Number(
    calendarDateInTimezone(clock.now, clock.timezone).slice(0, 4)
  );
  const year = monday.year === currentYear ? "" : `, ${monday.year}`;
  const end =
    monday.month === sunday.month ? `${sunday.day}` : `${sundayMonth} ${sunday.day}`;

  return `${mondayMonth} ${monday.day}${DASH}${end}${year}`;
}

type RelativeWeek = "this" | "last" | "other";

function relativeWeek(week: string, clock: PastWeekClock): RelativeWeek {
  const current = currentWeekStart(clock.now, clock.timezone);
  if (week === current) return "this";

  return week === earlierWeek(current) ? "last" : "other";
}

/** "This week" / "Last week"; any other week is named by its range. */
export function pastWeekHeading(week: string, clock: PastWeekClock): string {
  const relative = relativeWeek(week, clock);
  if (relative === "this") return "This week";

  return relative === "last" ? "Last week" : weekRangeLabel(week, clock);
}

export function appointmentCountLabel(count: number): string {
  return `${count} ${count === 1 ? "appointment" : "appointments"}`;
}

/**
 * The line beside the heading: the range when the heading does not already
 * name it, then the count. An empty week has no count; an errored week shows
 * only the range, and only for This week and Last week.
 */
export function pastWeekMeta(
  week: string,
  count: number,
  clock: PastWeekClock,
  options: { error?: boolean } = {}
): string {
  const parts: string[] = [];
  if (relativeWeek(week, clock) !== "other") parts.push(weekRangeLabel(week, clock));

  if (!options.error && count > 0) parts.push(appointmentCountLabel(count));

  return parts.join(" · ");
}

export function pastWeekEmptyText(week: string, clock: PastWeekClock): string {
  if (relativeWeek(week, clock) === "this")
    return "No ended appointments yet this week";

  return `No appointments ${weekRangeLabel(week, clock)}`;
}

export function pastWeekErrorText(week: string, clock: PastWeekClock): string {
  return `Couldn${APOSTROPHE}t load ${weekRangeLabel(week, clock)}.`;
}

/** F5b: a first load that failed before any week resolved. */
export const PAST_FIRST_LOAD_ERROR_TEXT = `Couldn${APOSTROPHE}t load past Appointments.`;
export const PAST_FIRST_LOAD_TEXT = "Loading past Appointments";
export const PAST_END_LINE_LABEL = "No earlier appointments";
export const PAST_EMPTY_TITLE = "No past Appointments yet";

export function pastEmptyDescription(viewingOther: boolean): string {
  return viewingOther
    ? "This RD's Appointments appear here after they end."
    : "Your Appointments appear here after they end.";
}

export function pastWeekLoadingText(target: string, clock: PastWeekClock): string {
  return `Loading ${weekRangeLabel(target, clock)}…`;
}

export type PagerDirection = "earlier" | "later";

export interface PastWeekPager {
  earlier: string | null;
  later: string | null;
}

export function pastWeekPager(
  week: string,
  flags: { hasEarlier: boolean; hasLater: boolean }
): PastWeekPager {
  return {
    earlier: flags.hasEarlier ? earlierWeek(week) : null,
    later: flags.hasLater ? laterWeek(week) : null,
  };
}

/** Starts with the visible label (WCAG 2.5.3), then names the target week. */
export function pagerAriaLabel(
  direction: PagerDirection,
  target: string | null,
  clock: PastWeekClock
): string {
  const label = direction === "earlier" ? "Earlier week" : "Later week";

  return `${label}, ${target === null ? "none" : weekRangeLabel(target, clock)}`;
}

/** The end line's sentence under the oldest week, or null for an empty one. */
export function earliestShownText(
  appointments: AppointmentListItem[],
  timezone: string,
  viewingOther: boolean
): string | null {
  const oldest = sortNewestFirst(appointments).at(-1);
  if (!oldest) return null;

  const date = new Intl.DateTimeFormat("en-US", {
    timeZone: timezone,
    weekday: "short",
    month: "short",
    day: "numeric",
    year: "numeric",
  }).format(new Date(oldest.startsAt));
  const owner = viewingOther ? `This RD${APOSTROPHE}s` : "Your";

  return `${owner} earliest appointment shown here is from ${date}.`;
}

export interface PastWeekRow {
  item: AppointmentListItem;
  /** "Fri 16" on a day's first row only. */
  dayLabel: string | null;
  /** A new day after the first, which draws a rule above it. */
  startsDay: boolean;
  timeLabel: string;
  /** The full date and time for screen readers. */
  srWhen: string;
}

function sortNewestFirst(appointments: AppointmentListItem[]): AppointmentListItem[] {
  return [...appointments].sort(
    (a, b) =>
      Date.parse(b.startsAt) - Date.parse(a.startsAt) || b.id.localeCompare(a.id)
  );
}

/** Newest first; within a day, latest time first; the day named once. */
export function pastWeekRows(
  appointments: AppointmentListItem[],
  timezone: string
): PastWeekRow[] {
  const day = new Intl.DateTimeFormat("en-US", {
    timeZone: timezone,
    weekday: "short",
    day: "numeric",
  });
  const time = new Intl.DateTimeFormat("en-US", {
    timeZone: timezone,
    hour: "numeric",
    minute: "2-digit",
  });
  const full = new Intl.DateTimeFormat("en-US", {
    timeZone: timezone,
    weekday: "long",
    month: "long",
    day: "numeric",
    year: "numeric",
  });
  let previousDate: string | null = null;

  return sortNewestFirst(appointments).map((item, index) => {
    const date = calendarDateInTimezone(item.startsAt, timezone);
    const isFirstOfDay = date !== previousDate;
    previousDate = date;
    const start = new Date(item.startsAt);

    return {
      item,
      dayLabel: isFirstOfDay ? dayLabelParts(day, start) : null,
      startsDay: isFirstOfDay && index > 0,
      timeLabel: time.format(start),
      srWhen: `${full.format(start)}, ${time.format(start)}`,
    };
  });
}

function dayLabelParts(format: Intl.DateTimeFormat, start: Date): string {
  const parts = format.formatToParts(start);
  const read = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find((part) => part.type === type)?.value;

  return `${read("weekday")} ${read("day")}`;
}

export type PastLiveState =
  | { kind: "first-loading" }
  | { kind: "first-error" }
  | { kind: "empty" }
  | { kind: "silent" }
  | { kind: "loading"; target: string }
  | { kind: "error"; week: string }
  | { kind: "ready"; week: string; count: number; hasEarlier: boolean };

/** The polite live region's Past message. Restoring a week is silent. */
export function pastWeekLiveText(state: PastLiveState, clock: PastWeekClock): string {
  switch (state.kind) {
    case "first-loading":
      return PAST_FIRST_LOAD_TEXT;
    case "first-error":
      return `Couldn${APOSTROPHE}t load past Appointments`;
    case "empty":
    case "silent":
      return "";
    case "loading":
      return `Loading ${weekRangeLabel(state.target, clock)}`;
    case "error":
      return `Couldn${APOSTROPHE}t load ${weekRangeLabel(state.week, clock)}`;
    case "ready":
      return readyLiveText(state, clock);
  }
}

function readyLiveText(
  state: Extract<PastLiveState, { kind: "ready" }>,
  clock: PastWeekClock
): string {
  const range = weekRangeLabel(state.week, clock);
  const shown =
    state.count > 0
      ? `Showing ${range}, ${appointmentCountLabel(state.count)}.`
      : `No appointments ${range}.`;

  return state.hasEarlier ? shown : `${shown} No earlier appointments.`;
}

/**
 * F7: one live region serves both views. While Past is on screen only Past
 * messages are spoken, so an Upcoming refetch behind it announces nothing.
 */
export function appointmentLiveText(input: {
  view: "upcoming" | "past";
  upcoming: { isFetchingMore: boolean; itemCount: number } | null;
  past: string;
}): string {
  if (input.view === "past") return input.past;

  if (!input.upcoming) return "";

  return input.upcoming.isFetchingMore
    ? "Loading more Appointments"
    : `Showing ${input.upcoming.itemCount} Appointments`;
}

export interface PastWeekRequest {
  week: string | null;
  showNonDefaultStatuses: boolean;
  timezone: string;
  rdId?: string;
}

/** The selection stays last, where keepsPreviousAppointmentRows reads it. */
export function pastWeekQueryKey(request: PastWeekRequest) {
  return [
    "appointments-past-week",
    request.week,
    request.showNonDefaultStatuses,
    request.timezone,
    request.rdId ?? null,
  ] as const;
}

export interface PastWeekBody {
  data: AppointmentPastWeekResponse;
}

/**
 * F4a: opening Past without a week makes one read. Its answer seeds the entry
 * for the week it resolved, so writing that week into the URL reuses the seed
 * rather than reading again. No seed when a week was asked for or none exists.
 */
export function pastWeekSeed(
  request: PastWeekRequest,
  body: PastWeekBody
): { key: ReturnType<typeof pastWeekQueryKey>; body: PastWeekBody } | null {
  if (request.week !== null) return null;

  if (body.data.status !== "available" || body.data.weekStart === null) return null;

  return { key: pastWeekQueryKey({ ...request, week: body.data.weekStart }), body };
}
